import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/ui/empty-state";
import { TruncatedText, useTruncationTooltip } from "@/components/ui/truncation-tooltip";
import type { FileResponse } from "@efes/shared";
import {
	createColumnHelper,
	flexRender,
	getCoreRowModel,
	useReactTable,
} from "@tanstack/react-table";
import { Trash2 } from "lucide-react";
import { useMemo } from "react";
import { LlmStatusIcon } from "./llm-status-icon";

const fileTypeLabels: Record<string, string> = {
	work_order: "Наряд",
	completion_act: "Акт",
	rate_book: "Книга расценок",
	estimate: "Смета",
	unclassified: "Не определён",
};

const statusLabels: Record<string, string> = {
	uploaded: "Загружен",
	recognized: "Распознан",
	parsed: "Загружен",
	error: "Ошибка",
};

function formatFileSize(bytes: number): string {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatPeriod(month: number | null, year: number | null): string {
	if (!month && !year) return "—";
	const m = month ? String(month).padStart(2, "0") : "??";
	const y = year ?? "????";
	return `${m}/${y}`;
}

const columnHelper = createColumnHelper<FileResponse>();

interface FileListProps {
	files: FileResponse[];
	onDelete: (file: FileResponse) => void;
	onOpenFile?: (file: FileResponse) => void;
	/** Множество выбранных fileId. Если задано — отображаются чекбоксы. */
	selectedIds?: ReadonlySet<number>;
	/** Колбэк выбора одной строки. */
	onToggleSelect?: (fileId: number) => void;
	/** Колбэк выбора всех видимых файлов (при клике на header-checkbox). */
	onToggleSelectAll?: (allSelected: boolean) => void;
}

/**
 * Таблица загруженных файлов с TanStack Table.
 * Колонки: чекбокс (опц.), имя, тип, объект, период, размер, статус, удаление.
 *
 * Если переданы `selectedIds` + `onToggleSelect` — в первой колонке
 * отображается чекбокс; в header-row — «select all» чекбокс.
 */
export function FileList({
	files,
	onDelete,
	onOpenFile,
	selectedIds,
	onToggleSelect,
	onToggleSelectAll,
}: FileListProps) {
	const selectionEnabled = selectedIds !== undefined && onToggleSelect !== undefined;
	const allSelected =
		selectionEnabled && files.length > 0 && files.every((f) => selectedIds.has(f.id));
	const someSelected = selectionEnabled && files.some((f) => selectedIds.has(f.id)) && !allSelected;

	const columns = useMemo(
		() => [
			...(selectionEnabled
				? [
						columnHelper.display({
							id: "select",
							header: () => (
								<input
									type="checkbox"
									aria-label="Выбрать все файлы"
									checked={allSelected}
									ref={(el) => {
										if (el) el.indeterminate = someSelected;
									}}
									onChange={() => onToggleSelectAll?.(allSelected)}
									className="h-4 w-4 cursor-pointer rounded border-slate-300 text-blue-600 focus:ring-blue-500"
								/>
							),
							cell: (info) => (
								<input
									type="checkbox"
									aria-label={`Выбрать ${info.row.original.originalName}`}
									checked={selectedIds.has(info.row.original.id)}
									onChange={(e) => {
										e.stopPropagation();
										onToggleSelect(info.row.original.id);
									}}
									onClick={(e) => e.stopPropagation()}
									className="h-4 w-4 cursor-pointer rounded border-slate-300 text-blue-600 focus:ring-blue-500"
								/>
							),
						}),
					]
				: []),
			columnHelper.accessor("originalName", {
				header: "Имя файла",
				cell: (info) => {
					const name = info.getValue();
					if (!onOpenFile) {
						return <TruncatedText value={name} className="font-medium text-slate-900 max-w-xs" />;
					}
					return <OpenFileButton name={name} onClick={() => onOpenFile(info.row.original)} />;
				},
			}),
			columnHelper.accessor("sheetName", {
				header: "Лист",
				cell: (info) => {
					const value = info.getValue();
					if (value == null || value === "") return <span className="text-slate-400">—</span>;
					return <TruncatedText value={value} className="text-slate-700 max-w-[140px]" />;
				},
			}),
			columnHelper.accessor("fileType", {
				header: "Тип",
				cell: (info) => (
					<Badge variant="tag" tone="neutral">
						{fileTypeLabels[info.getValue()] ?? info.getValue()}
					</Badge>
				),
			}),
			columnHelper.accessor("objectName", {
				header: "Объект",
				cell: (info) => {
					const value = info.getValue();
					if (value == null || value === "") return "—";
					return <TruncatedText value={value} className="text-slate-700 max-w-xs" />;
				},
			}),
			columnHelper.display({
				id: "period",
				header: "Период",
				cell: (info) => formatPeriod(info.row.original.periodMonth, info.row.original.periodYear),
			}),
			columnHelper.accessor("fileSize", {
				header: "Размер",
				cell: (info) => <span className="tabular-nums">{formatFileSize(info.getValue())}</span>,
			}),
			columnHelper.accessor("status", {
				header: "Статус",
				cell: (info) => {
					const status = info.getValue();
					const file = info.row.original;
					const color =
						status === "error"
							? "text-red-600"
							: status === "parsed"
								? "text-green-600"
								: "text-slate-600";
					return (
						<span className="inline-flex items-center">
							<span className={`text-xs font-medium ${color}`}>
								{statusLabels[status] ?? status}
							</span>
							{/* Sprint 4 / A6.5a: индикатор LLM-ревизии актов. */}
							<LlmStatusIcon fileId={file.id} fileType={file.fileType} fileStatus={status} />
						</span>
					);
				},
			}),
			columnHelper.accessor("createdAt", {
				header: "Дата",
				cell: (info) => {
					const d = new Date(info.getValue());
					return d.toLocaleDateString("ru-RU", {
						day: "2-digit",
						month: "2-digit",
						year: "2-digit",
						hour: "2-digit",
						minute: "2-digit",
					});
				},
			}),
			columnHelper.display({
				id: "actions",
				header: "",
				cell: (info) => (
					<button
						type="button"
						onClick={(event) => {
							event.stopPropagation();
							onDelete(info.row.original);
						}}
						className="p-1 text-slate-400 hover:text-red-500 transition-colors"
						title="Удалить"
					>
						<Trash2 className="h-4 w-4" />
					</button>
				),
			}),
		],
		[
			onDelete,
			onOpenFile,
			selectionEnabled,
			allSelected,
			someSelected,
			selectedIds,
			onToggleSelect,
			onToggleSelectAll,
		],
	);

	const table = useReactTable({
		data: files,
		columns,
		getCoreRowModel: getCoreRowModel(),
	});

	if (files.length === 0) {
		return <EmptyState title="Нет загруженных файлов" />;
	}

	return (
		<div className="overflow-x-auto rounded-lg border border-slate-200">
			<table className="w-full text-sm">
				<thead className="bg-slate-50 border-b border-slate-200">
					{table.getHeaderGroups().map((headerGroup) => (
						<tr key={headerGroup.id}>
							{headerGroup.headers.map((header) => (
								<th key={header.id} className="px-3 py-2.5 text-left font-medium text-slate-600">
									{header.isPlaceholder
										? null
										: flexRender(header.column.columnDef.header, header.getContext())}
								</th>
							))}
						</tr>
					))}
				</thead>
				<tbody className="divide-y divide-slate-100">
					{table.getRowModel().rows.map((row) => {
						const isSelected = selectedIds?.has(row.original.id) ?? false;
						return (
							<tr
								key={row.id}
								className={`transition-colors ${
									isSelected ? "bg-blue-50 hover:bg-blue-100" : "hover:bg-slate-50"
								}`}
							>
								{row.getVisibleCells().map((cell) => (
									<td key={cell.id} className="px-3 py-2 text-slate-700">
										{flexRender(cell.column.columnDef.cell, cell.getContext())}
									</td>
								))}
							</tr>
						);
					})}
				</tbody>
			</table>
		</div>
	);
}

function OpenFileButton({ name, onClick }: { name: string; onClick: () => void }) {
	const { ref, triggerProps, tooltipNode } = useTruncationTooltip(name);
	return (
		<>
			<button
				ref={ref as React.Ref<HTMLButtonElement>}
				type="button"
				onClick={onClick}
				className="block max-w-xs truncate text-left font-medium text-slate-900 underline-offset-2 hover:text-blue-700 hover:underline"
				{...triggerProps}
			>
				{name}
			</button>
			{tooltipNode}
		</>
	);
}
